/**
 * @description
 * BankLetteringService provides methods for querying open customer receivables,
 * fetching supported payment methods, and dispatching bank lettering & BOFIP
 * cryptographic sealing events to the backend event engine.
 *
 * Consumed by:
 * - BankLetteringComponent (`@app/sales/bank-lettering/bank-lettering.component`)
 *
 * Backend endpoints:
 * - GET `/sales/receivables/open`
 * - GET `/sales/bank-lettering/routing`
 * - POST `/event` (EventType.SALES_BANK_LETTERING = 1501 / ObjectType.PAYMENT = 15)
 */

import { Injectable, inject } from '@angular/core';

import { HttpClient, HttpParams } from '@angular/common/http';

import { Observable, map } from 'rxjs';

import { ValuesService } from '@app/common/values/values.service';
import { LoginService } from '@app/common/login.service';
import { ApiResponse, C_value, Event } from '@app/shared/_models/common';
import {
  BankLetteringPayload,
  BankLetteringResult,
  DeletteringPayload,
  LetteringRouting,
  OpenReceivable,
  OpenReceivablesQueryParams,
  ReceivablePaymentAllocation
} from '@app/shared/_models/sales';

import { environment } from '../../../environments/environment';
import * as constants from '@app/shared/constants';

@Injectable({
  providedIn: 'root'
})
export class BankLetteringService {
  private http = inject(HttpClient);
  private valuesService = inject(ValuesService);
  private loginService = inject(LoginService);

  /**
   * Base server URL from environment configuration.
   */
  private readonly baseUrl = environment.server_url;

  /**
   * Fetches open and partially settled receivables eligible for bank lettering.
   *
   * @param params Optional filter parameters (payerId, search query, limit, offset).
   * @returns Observable of OpenReceivable array.
   */
  getOpenReceivables(params: OpenReceivablesQueryParams = {}): Observable<OpenReceivable[]> {
    let httpParams = new HttpParams();

    if (params.payerId !== undefined && params.payerId !== null && params.payerId > 0) {
      httpParams = httpParams.set('payer_id', params.payerId.toString());
    }

    if (params.includeTp !== undefined && params.includeTp !== null) {
      httpParams = httpParams.set('include_tp', params.includeTp ? 'true' : 'false');
    }

    if (params.search && params.search.trim().length > 0) {
      httpParams = httpParams.set('search', params.search.trim());
    }

    if (params.limit !== undefined && params.limit !== null && params.limit > 0) {
      httpParams = httpParams.set('limit', params.limit.toString());
    }

    if (params.offset !== undefined && params.offset !== null) {
      httpParams = httpParams.set('offset', params.offset.toString());
    }

    return this.http.get<OpenReceivable[]>(`${this.baseUrl}/sales/receivables/open`, {
      params: httpParams
    });
  }

  /**
   * Retrieves active payment methods from reference group 7 (`C_PAYMENT_METHOD`).
   *
   * @returns Observable list of payment methods.
   */
  getPaymentMethods(): Observable<C_value[]> {
    return this.valuesService.getValues$(constants.C_PAYMENT_METHOD).pipe(
      map((items: C_value[]) => (items || []).filter((item) => item.id !== undefined && item.id !== null && item.id > 0 && item.is_active !== false))
    );
  }

  // Poste d'arrivée de chaque mode lettrable depuis le poste courant (en-tête X-Station-Id) : le serveur arbitre, l'écran affiche.
  getRouting$(): Observable<LetteringRouting> {
    return this.http.get<LetteringRouting>(`${this.baseUrl}/sales/bank-lettering/routing`);
  }

  /**
   * Dispatches a sales bank lettering event to the backend for atomic execution,
   * receivable balance updating, and BOFIP cryptographic sealing.
   *
   * @param payload Lettering payload containing bank statement info and receivable allocations.
   * @returns Observable of ApiResponse with BankLetteringResult data.
   */
  submitBankLettering(payload: BankLetteringPayload): Observable<ApiResponse> {
    const userId = this.loginService.loggeduser?.id || 1;
    const event: Event = {
      id: 0,
      typ: constants.EventType.SALES_BANK_LETTERING,
      userid: userId,
      objectid: 0,
      objecttyp: constants.ObjectType.PAYMENT,
      data: payload
    };

    return this.http.post<ApiResponse>(`${this.baseUrl}/event`, event, {
      headers: constants.jsonheader
    });
  }

  /**
   * Fetches active payment allocations for a given receivable.
   *
   * @param receivableId The receivable ID to query allocations for.
   * @returns Observable of active ReceivablePaymentAllocation array.
   */
  getReceivableAllocations(receivableId: number): Observable<ReceivablePaymentAllocation[]> {
    return this.http.get<ReceivablePaymentAllocation[]>(`${this.baseUrl}/sales/receivables/${receivableId}/allocations`);
  }

  /**
   * Dispatches a sales de-lettering event (contre-passation d'imputation) to the backend.
   * Inserts an offsetting negative allocation and seals it cryptographically according to BOFIP ISCA.
   *
   * @param payload Delettering payload with target receivable, payment, reason, and optional amount.
   * @returns Observable of ApiResponse with DeletteringResult.
   */
  deletterAllocation(payload: DeletteringPayload): Observable<ApiResponse> {
    const userId = this.loginService.loggeduser?.id || 1;
    const event: Event = {
      id: 0,
      typ: constants.EventType.SALES_DELETTERING,
      userid: userId,
      objectid: 0,
      objecttyp: constants.ObjectType.PAYMENT,
      data: payload
    };

    return this.http.post<ApiResponse>(`${this.baseUrl}/event`, event, {
      headers: constants.jsonheader
    });
  }
}
